import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { isLogin, getUserInfo, removeToken } from '../utils/auth';
import { request } from '../utils/request';
import { getMySucculentList, getPublicSucculentList, SucculentPlant } from '../utils/flowerApi';
import { config } from '../config';
import { SucculentCard } from '../components/SucculentCard';
import { ImageUpload } from '../components/ImageUpload';
import { Toast } from '../components/Toast';
import styles from './SucculentList.module.css';

/**
 * 多肉列表页 — 公开区块 + 我的多肉
 */
export const SucculentList: React.FC = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState<any>(null);
  const [authChecked, setAuthChecked] = useState(false);

  // 公开列表 — 首页只取前几条预览
  const [publicItems, setPublicItems] = useState<SucculentPlant[]>([]);

  // 我的列表
  const [mineItems, setMineItems] = useState<SucculentPlant[]>([]);
  const [mineTotal, setMineTotal] = useState(0);

  // 上传弹窗
  const [showUpload, setShowUpload] = useState(false);

  // 搜索提示
  const [searchToast, setSearchToast] = useState(false);

  // 回到顶部
  const [showBackToTop, setShowBackToTop] = useState(false);

  const isLoggedIn = !!userInfo;

  // 公开数据不依赖登录，直接加载
  useEffect(() => {
    getPublicSucculentList(1, 10).then(({ items }) => setPublicItems(items)).catch(() => {});
  }, []);

  const fetchMine = useCallback(async () => {
    try {
      const { items, total } = await getMySucculentList(1, 50);
      setMineItems(items);
      setMineTotal(total);
    } catch (err) {
      console.error('Failed to fetch my list:', err);
    }
  }, []);

  // 登录状态检查
  useEffect(() => {
    if (authChecked) return;
    const initLogin = async () => {
      if (isLogin()) {
        const cachedUser = getUserInfo();
        if (cachedUser) {
          setUserInfo(cachedUser);
          setAuthChecked(true);
          return;
        }
        try {
          const user = await request(config.USER_URL);
          setUserInfo(user);
        } catch {
          removeToken();
        }
      }
      setAuthChecked(true);
    };
    initLogin();
  }, [authChecked]);

  // 登录后加载个人列表
  useEffect(() => {
    if (isLoggedIn) {
      fetchMine();
    }
  }, [isLoggedIn, fetchMine]);

  // Scroll tracking
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setShowBackToTop(y > 500);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogin = async () => {
    try {
      const response = await fetch(
        `${config.API_BASE}${config.CASDOOR_URL}?redirect_uri=${encodeURIComponent(config.REDIRECT_URI)}`,
        { method: 'GET', redirect: 'manual' }
      );
      const location = response.headers.get('Location');
      if (location) {
        window.location.href = location;
      }
    } catch { /* ignore */ }
  };

  const handleUploadSuccess = () => {
    fetchMine();
  };

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <h1 className={styles.logo}>我的多肉</h1>
        <div className={styles.headerRight}>
          <button className={styles.settingsBtn} onClick={() => isLoggedIn ? navigate('/settings') : handleLogin()} title={isLoggedIn ? '设置' : '登录'}>
            &#9881;
          </button>
        </div>
      </header>

      {/* Toolbar */}
      {isLoggedIn && (
        <div className={styles.toolbar}>
          <button className={styles.toolBtn} onClick={() => setShowUpload(true)} title="上传">
            &#128247;
          </button>
          <button className={styles.toolBtn} onClick={() => setSearchToast(true)} title="搜索">
            &#128269;
          </button>
        </div>
      )}

      {/* Public section — single row, horizontal scroll */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>公开的多肉</h2>
          <button className={styles.moreBtn} onClick={() => navigate('/public')}>
            更多 &gt;&gt;
          </button>
        </div>
        {publicItems.length > 0 ? (
          <div className={styles.publicStrip}>
            {publicItems.map((plant) => (
              <SucculentCard
                key={plant.succulent_plant_id}
                plant={plant}
                variant="public"
                onClick={() => navigate(`/succulents/${plant.succulent_plant_id}`, { state: { plant, source: 'public' } })}
              />
            ))}
          </div>
        ) : (
          <p className={styles.sectionHint}>暂无公开多肉</p>
        )}
      </section>

      {/* My Section — login required */}
      {isLoggedIn ? (
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>我的多肉</h2>
            <span className={styles.mineCount}>
              共 {mineTotal} 棵
            </span>
          </div>
          {mineItems.length === 0 ? (
            <div className={styles.empty}>
              <p>还没有添加多肉，点击上方「上传照片」开始吧</p>
            </div>
          ) : (
            <div className={styles.mineGrid}>
              {mineItems.map((plant) => (
                <SucculentCard
                  key={plant.succulent_plant_id}
                  plant={plant}
                  variant="mine"
                  onClick={() => navigate(`/succulents/${plant.succulent_plant_id}`, { state: { plant } })}
                />
              ))}
            </div>
          )}
        </section>
      ) : (
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>我的多肉</h2>
          </div>
          <div className={styles.loginPrompt}>
            <p>登录后查看和管理你的多肉</p>
          </div>
        </section>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <ImageUpload
          onSuccess={handleUploadSuccess}
          onClose={() => setShowUpload(false)}
        />
      )}

      {/* Search Toast */}
      {searchToast && (
        <Toast message="搜索功能开发中，敬请期待" onClose={() => setSearchToast(false)} />
      )}

      {/* Footer */}
      <footer className={styles.footer}>
        <span>ICP备案号:</span>
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
          闽ICP备2025102433号-1
        </a>
      </footer>

      {/* Back to top */}
      {showBackToTop && (
        <button className={styles.backToTop} onClick={scrollToTop}>
          &#8593;
        </button>
      )}
    </div>
  );
};
